import { createAsyncThunk } from "@reduxjs/toolkit";
import i18next from "i18next";
import { toast } from "react-toastify";
import { apiCall } from "services/api";

import { toFormData } from "utils/files";

import {
  TaskFormData,
  TaskRequirementsFormData,
  TaskStatusFormData,
  TasksFilters,
} from "./types";

export const createTask = createAsyncThunk(
  "/tasks/create",
  async (task: TaskFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().post("/tasks", task);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "taskForm.taskCreated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const createMeetingTask = createAsyncThunk(
  "/tasks/meeting/create",
  async (
    values: { task: TaskFormData; meetingId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().post(
        `/meetings/${values.meetingId}/tasks`,
        values.task
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "taskForm.taskCreated"
        )
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTask = createAsyncThunk(
  "/tasks/get/:id",
  async (taksId: string | number, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/tasks/${taksId}`);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTask = createAsyncThunk(
  "/tasks/update/",
  async (task: TaskFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(`/tasks/${task.id}`, task);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateMyTask = createAsyncThunk(
  "/my/tasks/update/",
  async (task: TaskFormData, { rejectWithValue }) => {
    try {
      const response = await apiCall().patch(`/my/tasks/${task.id}`, task);
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserTasks = createAsyncThunk(
  "/tasks/user",
  async (filters: TasksFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/my/tasks", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getAllTasks = createAsyncThunk(
  "/tasks/all",
  async (filters: TasksFilters, { rejectWithValue }) => {
    try {
      const response = await apiCall().get("/tasks", { params: filters });

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTaskTypes = createAsyncThunk(
  "/tasks/types",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Task\\Enums\\TaskType"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTaskPriorities = createAsyncThunk(
  "/tasks/priorities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiCall({}).get(
        "/enums/single?enum=Domain\\Task\\Enums\\TaskPriority"
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadTaskFile = createAsyncThunk(
  "/tasks/attachment/add",
  async (
    {
      taskId,
      file,
    }: { taskId: string; file: { attachment: File; description: string } },
    { rejectWithValue }
  ) => {
    const formData = toFormData({
      attachment: file.attachment,
      description: file.description,
    });
    try {
      const response = await apiCall().post(
        `/tasks/${taskId}/attachments`,
        formData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTaskFile = createAsyncThunk(
  "/tasks/attachment/get",
  async (
    { taskId, attachmentId }: { taskId: string; attachmentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `/tasks/${taskId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTaskFile = createAsyncThunk(
  "/tasks/attachment/delete",
  async (
    { taskId, attachmentId }: { taskId: string; attachmentId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/tasks/${taskId}/attachments/${attachmentId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getMyTask = createAsyncThunk(
  "/my/tasks/:id",
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await apiCall().get(`/my/tasks/${taskId}`);

      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTaskRequirements = createAsyncThunk(
  "/tasks/:id/done",
  async (
    { taskId, data, toastText }: TaskRequirementsFormData,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(`/my/tasks/${taskId}`, data);
      toastText &&
        toast.success(
          i18next.getResource(i18next.language, "translation", toastText)
        );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateTaskStatus = createAsyncThunk(
  "/my/tasks/:id/status",
  async (
    { taskId, data, toastText }: TaskStatusFormData,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().patch(
        `/my/tasks/${taskId}/status`,
        data
      );
      toastText &&
        toast.success(
          i18next.getResource(i18next.language, "translation", toastText)
        );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMeetingTask = createAsyncThunk(
  "/tasks/meetings/:id/delete",
  async (
    values: { meetingId: number; taskId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/meetings/${values.meetingId}/tasks/${values.taskId}`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "taskForm.taskDeleted"
        )
      );
      return { response: response.data, taskId: values.taskId };
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTask = createAsyncThunk(
  "/tasks/:id/delete",
  async (taskId: number, { rejectWithValue }) => {
    try {
      const response = await apiCall().delete(`/tasks/${taskId}`);
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "taskForm.taskDeleted"
        )
      );
      return { response: response.data, taskId: taskId };
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteTaskRequirement = createAsyncThunk(
  "/tasks/:id/requirements/:requirementId/delete",
  async (
    values: { taskId: number; requirementId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().delete(
        `/tasks/${values.taskId}/requirements/${values.requirementId}`
      );
      toast.success(
        i18next.getResource(
          i18next.language,
          "translation",
          "taskForm.requirementDeleted"
        )
      );
      return { response: response.data, taskId: values.taskId };
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserTaskAssignment = createAsyncThunk(
  "/tasks/:id/assignment/:userId",
  async (
    { taskId, userId }: { taskId: number; userId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiCall().get(
        `/tasks/${taskId}/assignment/${userId}`
      );
      return response.data;
    } catch (error: any) {
      toast.error(error.response.data.message);
      return rejectWithValue(error.response.data);
    }
  }
);
